/**
 * Evergreen clearance guides. Each one targets a search people actually make ("home
 * depot penny items", "target clearance schedule") that the brand-only homepage can
 * never rank for, and hands the reader to the matching live feed.
 *
 * Content rules: retailers don't publish their markdown codes, so anything that comes
 * from shopper communities is written as "commonly reported", never as policy. Plain
 * strings only — both the React page and the build-time pre-renderer render these.
 */
import { pennyMealsUrl } from "./pennyMeals";

export interface GuideSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface Guide {
  slug: string;
  /** <title> — keep under ~60 characters where possible. */
  title: string;
  h1: string;
  description: string;
  /** ISO date the content was last reviewed. */
  updated: string;
  retailerName: string;
  /** /deals/<slug> live feed, when the retailer has one. */
  dealsPath?: string;
  /** /shop/<retailer> server-rendered catalog hub, when it has pages. */
  shopPath?: string;
  /** Feature page that fits the guide best. */
  featurePath: string;
  intro: string;
  sections: GuideSection[];
  faq: { q: string; a: string }[];
  /** Grocery tie-in to Penny Meals (pennymeals.com), shown after the sections. */
  pennyMeals?: PennyMealsCallout;
}

/** A short paragraph whose link parts point at pennymeals.com. */
export interface PennyMealsCallout {
  heading: string;
  body: Array<string | { text: string; href: string }>;
}

export const GUIDES: Guide[] = [
  {
    slug: "home-depot-penny-items",
    title: "Home Depot Penny Items: How Penny Shopping Works",
    h1: "Home Depot penny items: how penny shopping works",
    description:
      "What Home Depot penny items are, why they ring up at $0.01, the clearance price endings shoppers watch for, and how to find penny candidates at stores near you.",
    updated: "2026-09-30",
    retailerName: "Home Depot",
    dealsPath: "/deals/home-depot",
    shopPath: "/shop/home_depot",
    featurePath: "/features/in-store-deals",
    intro:
      "A Home Depot \"penny item\" is a product that rings up at $0.01 at the register. It isn't a sale — it's an item the store has been told to pull from the floor, usually after it has gone through every clearance markdown. If it's still on the shelf when you scan it, the register may charge a penny.",
    sections: [
      {
        heading: "Why items go to a penny",
        paragraphs: [
          "Home Depot clears discontinued and seasonal products in stages. When an item reaches the end of its clearance cycle, the system marks it for removal and the price drops to $0.01 so it can't be sold at a normal price by mistake. Staff are supposed to pull those items for return, donation or disposal — but not every unit gets pulled, which is where penny shoppers come in.",
          "Because the penny price exists for removal rather than sale, penny items are almost never tagged on the shelf and don't show at $0.01 on homedepot.com. You find them by scanning.",
        ],
      },
      {
        heading: "Clearance price endings shoppers watch",
        paragraphs: [
          "Home Depot doesn't publish its markdown codes, but penny shoppers commonly report that the cents on a yellow clearance tag hint at where an item is in its cycle:",
        ],
        bullets: [
          "Prices ending in .06 — an earlier clearance markdown, with more reductions likely to come.",
          "Prices ending in .03 — commonly treated as the final markdown before an item is pulled.",
          "Prices ending in .01 at the register — a penny item.",
          "These patterns vary by store and department and aren't guaranteed. Treat them as hints, not rules.",
        ],
      },
      {
        heading: "How to find penny items",
        bullets: [
          "Scan clearance items with the Home Depot app's in-store mode. Items marked for removal may show as unavailable or with no price rather than $0.01, so an odd result on a clearance item is worth a closer look.",
          "Look at end caps, back shelves and bays that are being reset — penny items cluster where old products are waiting to be pulled.",
          "Follow items that sat at a .03 price: those are the ones most likely to drop next.",
          "Use a tracker that watches clearance at many stores at once so you know which of your local stores are worth the drive.",
        ],
      },
      {
        heading: "Store policy and etiquette",
        paragraphs: [
          "Officially, Home Depot does not sell penny items, and a cashier or manager can decline the sale. Ask politely, don't hide items, don't pester staff for penny lists, and accept a no. Stores that feel gamed tend to tighten up for everyone.",
        ],
      },
      {
        heading: "How DealHawk helps",
        paragraphs: [
          "DealHawk tracks Home Depot clearance at the stores near you and flags items that look like they've hit the end of their markdown cycle — clearance items still showing on-hand inventory but no longer sellable online. You get a shortlist of likely penny and deep-clearance items per store instead of walking every aisle.",
        ],
      },
    ],
    faq: [
      {
        q: "What is a Home Depot penny item?",
        a: "It's a product that rings up at $0.01 because Home Depot has marked it for removal from the sales floor, usually after its final clearance markdown. It isn't advertised and isn't tagged at a penny on the shelf.",
      },
      {
        q: "Does Home Depot have to sell me a penny item?",
        a: "No. Penny items are meant to be pulled, not sold, so a cashier or manager can refuse the sale. Many stores do sell them when they turn up at the register, but it's at the store's discretion.",
      },
      {
        q: "Is there a Home Depot penny list?",
        a: "Home Depot doesn't publish one. Shopper communities share items they've found, but penny items differ by store, so a national list is only a starting point. Checking the clearance at your own stores is more reliable.",
      },
      {
        q: "What does a price ending in .03 mean at Home Depot?",
        a: "Penny shoppers commonly report that a clearance price ending in .03 is the final markdown before the item is pulled. Home Depot doesn't confirm this and it isn't consistent everywhere.",
      },
    ],
  },
  {
    slug: "dollar-general-penny-shopping",
    title: "Dollar General Penny List: How Penny Shopping Works",
    h1: "Dollar General penny shopping: how the penny list works",
    description:
      "How Dollar General penny items work, why they scan at $0.01, when new penny items usually appear, and how to shop them without breaking store rules.",
    updated: "2026-09-30",
    retailerName: "Dollar General",
    featurePath: "/features/in-store-deals",
    intro:
      "Dollar General penny items are products that scan at $0.01 because the chain has told stores to remove them — discontinued items, old packaging, or seasonal stock past its date. They're meant to be pulled from the shelf, but when a unit is missed, it can ring up for a penny.",
    sections: [
      {
        heading: "Why Dollar General items go to a penny",
        paragraphs: [
          "When Dollar General discontinues an item or resets a section, the system drops that item's price to $0.01 as a signal to pull it. The penny price is an internal flag, not a promotion, so there are no penny signs and nothing in the weekly ad.",
          "Before an item goes to a penny it usually spends time on clearance, often in the clearance aisle or on an end cap with a reduced price sticker.",
        ],
      },
      {
        heading: "When new penny items show up",
        paragraphs: [
          "Shopper communities commonly report that new penny items take effect early in the week, with Tuesday the most-cited day. Stores that are slow to reset keep penny items on the shelf longer, so smaller or busier stores can be better hunting grounds.",
        ],
      },
      {
        heading: "How to find Dollar General penny items",
        bullets: [
          "Start with the clearance aisle and seasonal leftovers — those are the items closest to a penny.",
          "Check the DG app's price scanner before checkout. If an item won't scan or shows an unexpected price, it may have been flagged.",
          "Compare shared penny lists with what's actually in your store; items differ between stores and change weekly.",
          "Look for old packaging next to new packaging — the old version is often the one being pulled.",
        ],
      },
      {
        heading: "Rules and etiquette",
        paragraphs: [
          "Dollar General's official position is that penny items aren't for sale, and employees may decline to ring them up. Don't ask staff to look up penny items, don't clear shelves, and be ready to put items back if the cashier says no.",
        ],
      },
      {
        heading: "How DealHawk helps",
        paragraphs: [
          "DealHawk tracks Dollar General prices and digital coupons at the stores you choose and highlights deep markdowns, including items that net close to free after coupons and cash back. You see what's worth the trip before you go.",
        ],
      },
    ],
    pennyMeals: {
      heading: "Turning grocery markdowns into dinner",
      body: [
        "A lot of what goes on clearance at dollar and grocery stores is food. ",
        { text: "Penny Meals", href: pennyMealsUrl() },
        ", from the DealHawk team, turns this week's grocery markdowns at your stores into dinners under a few dollars a serving. Any DealHawk plan includes Penny Meals Plus.",
      ],
    },
    faq: [
      {
        q: "What day does Dollar General release penny items?",
        a: "Dollar General doesn't announce penny items. Shoppers most often report new penny items appearing early in the week, especially Tuesday, but timing varies by store.",
      },
      {
        q: "Can Dollar General refuse to sell a penny item?",
        a: "Yes. Penny items are flagged for removal, so a store can decline the sale. Many shoppers find some stores will sell them and others won't.",
      },
      {
        q: "Where can I find the Dollar General penny list?",
        a: "There's no official list. Shopper groups share weekly lists, but what's actually on the shelf depends on your store, so use the lists as a guide and check in person.",
      },
    ],
  },
  {
    slug: "walmart-hidden-clearance",
    title: "Walmart Hidden Clearance: How to Find Unadvertised Markdowns",
    h1: "Walmart hidden clearance: how to find unadvertised markdowns",
    description:
      "Why Walmart clearance prices are often lower at the register than on the shelf, how clearance differs from Rollback, and how to find hidden clearance at your local store.",
    updated: "2026-09-30",
    retailerName: "Walmart",
    dealsPath: "/deals/walmart",
    shopPath: "/shop/walmart",
    featurePath: "/features/in-store-deals",
    intro:
      "\"Hidden clearance\" is the gap between the price on a Walmart shelf tag and the lower price the item actually rings up at. Walmart marks items down store by store, and the shelf tags don't always catch up, so some of the best clearance at your store isn't labeled at all.",
    sections: [
      {
        heading: "Why Walmart clearance is \"hidden\"",
        paragraphs: [
          "Clearance at Walmart is set locally: each store marks down items it needs to sell through, so the same product can be full price in one store and 75% off in the next. Price changes hit the register first, while re-tagging the shelf is a manual job that often lags or gets skipped.",
          "Clearance items also get moved — to an end cap, a clearance shelf, or back into the wrong spot — which makes them easy to miss.",
        ],
      },
      {
        heading: "Clearance vs Rollback",
        bullets: [
          "Rollback: a temporary price cut, usually for a set period, after which the price may go back up. Marked with Rollback signage.",
          "Clearance: a markdown to sell through stock the store no longer wants. Prices tend to keep dropping until the item is gone, and they differ by store.",
          "Online vs in store: walmart.com prices are separate from store prices, so an online search won't tell you what your store's clearance price is.",
        ],
      },
      {
        heading: "How to find hidden clearance",
        bullets: [
          "Scan items with the Walmart app in store — it shows your store's current price, which may be lower than the tag.",
          "Check seasonal, electronics, toys and home aisles after holidays and at the end of each season, when resets push old stock to clearance.",
          "Look at the ends of aisles and on top shelves, where marked-down items are often parked.",
          "Use a tool that checks prices across several nearby stores, since clearance varies so much between locations.",
        ],
      },
      {
        heading: "How DealHawk helps",
        paragraphs: [
          "DealHawk scans Walmart prices at the stores near you and shows items priced well below their regular price — including markdowns that aren't tagged on the shelf — along with stock, aisle location when available, and price history, so you can see whether a deal is real before you go.",
        ],
      },
    ],
    pennyMeals: {
      heading: "Walmart grocery markdowns",
      body: [
        "Clearance at Walmart isn't only toys and home goods. Grocery prices drop too, and ",
        { text: "Penny Meals", href: pennyMealsUrl("/walmart") },
        ", from the DealHawk team, turns this week's Walmart grocery markdowns into dinners under a few dollars a serving. See ",
        { text: "Walmart dinner ideas", href: pennyMealsUrl("/walmart/dinner-ideas") },
        " built from what's on sale. Any DealHawk plan includes Penny Meals Plus.",
      ],
    },
    faq: [
      {
        q: "What is Walmart hidden clearance?",
        a: "Items that ring up at a clearance price even though the shelf tag still shows the regular price. It happens because store-level markdowns reach the register before the shelf is re-tagged.",
      },
      {
        q: "Are Walmart clearance prices the same at every store?",
        a: "No. Walmart sets clearance by store, so the same item can have very different prices at stores a few miles apart.",
      },
      {
        q: "Will Walmart honor the lower price at the register?",
        a: "The register price is the price you pay. If a shelf tag shows a lower price than the register, ask the store — policies on tag errors vary.",
      },
    ],
  },
  {
    slug: "target-clearance-schedule",
    title: "Target Clearance Schedule & Markdown Percentages",
    h1: "Target clearance schedule and markdown percentages",
    description:
      "How Target clearance markdowns progress (30%, 50%, 70% and 90% off), the department markdown schedule shoppers commonly report, and how to catch the deepest discounts.",
    updated: "2026-09-30",
    retailerName: "Target",
    dealsPath: "/deals/target",
    featurePath: "/features/in-store-deals",
    intro:
      "Target clearance follows a fairly predictable path: items are marked down in steps, and each department tends to be marked on a particular day of the week. Target doesn't publish the schedule, but shoppers have tracked it for years, and knowing it helps you show up on the right day.",
    sections: [
      {
        heading: "How Target markdowns progress",
        paragraphs: [
          "Clearance items get a red or orange sticker showing the percentage off. Markdowns usually step through 30%, 50% and 70% off, with some items — often seasonal — reaching 90% off before they're pulled. The top-left corner of the sticker shows the discount; the price is what you pay.",
          "Each step typically lasts a week or two, but popular items may sell out before the next markdown, so waiting for a deeper discount is a gamble.",
        ],
      },
      {
        heading: "The commonly reported department schedule",
        paragraphs: [
          "Shoppers widely report that Target marks down clearance by department on these days. Individual stores can differ, so treat this as a starting point:",
        ],
        bullets: [
          "Monday: electronics, entertainment, baby, kitchen, stationery, books, food",
          "Tuesday: women's clothing, domestics, pets, market/food",
          "Wednesday: men's and kids' clothing, health and beauty, shoes, diapers",
          "Thursday: home décor, housewares, toys, sporting goods, lingerie, luggage, cosmetics",
          "Friday: automotive, hardware, bath, jewelry, accessories",
        ],
      },
      {
        heading: "When the biggest clearance happens",
        bullets: [
          "After each holiday — seasonal aisles usually move to clearance within days and step down quickly.",
          "At the end of each season, when apparel, outdoor and home collections reset.",
          "During store remodels and planogram resets, when whole sections are cleared.",
        ],
      },
      {
        heading: "How DealHawk helps",
        paragraphs: [
          "DealHawk watches Target prices and shows markdowns online and near you, with price history, so you can see when an item has actually dropped — and set alerts for the items you're waiting on instead of checking every week.",
        ],
      },
    ],
    pennyMeals: {
      heading: "Food markdowns",
      body: [
        "Food and market items are on the commonly reported schedule too. For grocery deals, ",
        { text: "Penny Meals", href: pennyMealsUrl() },
        ", from the DealHawk team, turns this week's grocery markdowns at your stores into dinners under a few dollars a serving.",
      ],
    },
    faq: [
      {
        q: "What day does Target mark down clearance?",
        a: "It depends on the department. Shoppers commonly report electronics and baby on Monday, women's clothing on Tuesday, men's and kids' clothing and health & beauty on Wednesday, home and toys on Thursday, and automotive and hardware on Friday. Target doesn't publish an official schedule.",
      },
      {
        q: "What are Target's clearance percentages?",
        a: "Clearance usually steps through 30%, 50% and 70% off, and some items reach 90% off before they're removed.",
      },
      {
        q: "Does Target clearance go to 90% off?",
        a: "Some items, especially seasonal merchandise, reach 90% off near the end of their clearance cycle. Many sell out before then.",
      },
    ],
  },
  {
    slug: "lowes-clearance",
    title: "Lowe's Clearance: How Markdowns & Yellow Tags Work",
    h1: "How Lowe's clearance works",
    description:
      "Where to find Lowe's clearance, what yellow tags mean, why prices differ by store, and how to catch discontinued tools, appliances and seasonal items at the lowest price.",
    updated: "2026-09-30",
    retailerName: "Lowe's",
    dealsPath: "/deals/lowes",
    shopPath: "/shop/lowes",
    featurePath: "/features/price-alerts",
    intro:
      "Lowe's marks down discontinued, seasonal and overstocked items store by store, and the deepest clearance usually shows up at the end of a season or during a reset. The trick is knowing where it lands and when to check back.",
    sections: [
      {
        heading: "What Lowe's clearance looks like",
        paragraphs: [
          "Clearance items at Lowe's are marked with yellow tags on the shelf or yellow stickers on the product. Because markdowns are set locally, the same item can be clearance at one store and full price at the next, and the online price may not match either.",
        ],
      },
      {
        heading: "Where and when to look",
        bullets: [
          "End caps and the clearance area near the back of the store.",
          "Seasonal departments right after a season ends — outdoor, grills, holiday décor, patio.",
          "Tools and hardware when a brand or model line is replaced.",
          "Appliances with scratches, dents or open boxes, which are often discounted as-is.",
        ],
      },
      {
        heading: "Tips for getting the best price",
        bullets: [
          "Check the price in the Lowe's app for your store; clearance can be lower than the tag.",
          "Ask whether an item is discontinued — discontinued stock keeps getting marked down until it's gone.",
          "Compare nearby stores, since stock and markdowns vary widely.",
        ],
      },
      {
        heading: "How DealHawk helps",
        paragraphs: [
          "DealHawk tracks Lowe's markdowns and keeps a price history for each item, so you can see whether a price is the lowest it's been and get alerted when something you want drops.",
        ],
      },
    ],
    faq: [
      {
        q: "What does a yellow tag mean at Lowe's?",
        a: "A yellow tag or sticker marks a clearance item — usually discontinued, seasonal or overstocked merchandise priced below its regular price.",
      },
      {
        q: "Are Lowe's clearance prices the same online and in store?",
        a: "Often not. Store clearance is set locally and may be lower than the online price, and it can differ between nearby stores.",
      },
    ],
  },
  {
    slug: "best-buy-open-box",
    title: "Best Buy Open-Box & Clearance: What Each Condition Means",
    h1: "Best Buy open-box and clearance: what each condition means",
    description:
      "What Best Buy's open-box Excellent, Good and Fair conditions mean, how open-box compares with clearance, and how to find the best electronics markdowns.",
    updated: "2026-09-30",
    retailerName: "Best Buy",
    dealsPath: "/deals/best-buy",
    shopPath: "/shop/bestbuy",
    featurePath: "/features/price-alerts",
    intro:
      "Best Buy sells returned and display items as open-box at a discount, and clears discontinued models through regular markdowns. Both can be great deals — if you know what the condition labels mean and how prices move.",
    sections: [
      {
        heading: "Open-box conditions",
        bullets: [
          "Excellent: looks new, with no noticeable cosmetic damage, and includes all original parts and accessories.",
          "Good: may show minor cosmetic wear, and may be missing non-essential accessories.",
          "Fair: may have noticeable cosmetic damage and may be missing non-essential accessories.",
          "Always check the listing's notes — what's included can vary by item.",
        ],
      },
      {
        heading: "Open-box vs clearance",
        paragraphs: [
          "Open-box pricing depends on the condition of a specific unit at a specific store, so two open-box units of the same model can have different prices. Clearance applies to new items Best Buy is discontinuing; those prices tend to drop as a model is replaced and stock runs down.",
        ],
      },
      {
        heading: "When Best Buy deals are best",
        bullets: [
          "When a new model launches and the previous generation is cleared.",
          "After the holidays, when return volume pushes more open-box stock onto shelves.",
          "Around major sale events, when regular prices on current models drop too.",
        ],
      },
      {
        heading: "How DealHawk helps",
        paragraphs: [
          "DealHawk tracks Best Buy price drops and open-box listings with price history, so you can tell whether a markdown is actually a low and get alerted when a model you're watching drops.",
        ],
      },
    ],
    faq: [
      {
        q: "Is Best Buy open-box worth it?",
        a: "Often, yes — especially open-box Excellent, which looks new and includes all parts. Compare the open-box price with the current sale price on a new unit, since sales can close the gap.",
      },
      {
        q: "What's the difference between open-box Excellent and Good at Best Buy?",
        a: "Excellent items look new and include all original parts. Good items may show minor wear and may be missing non-essential accessories.",
      },
    ],
  },
  {
    slug: "costco-price-codes",
    title: "Costco Price Codes: What .97, .00 & the Asterisk Mean",
    h1: "Costco price codes explained",
    description:
      "What Costco prices ending in .97, .00 and .88 are commonly reported to mean, what the asterisk on a price sign signals, and how to use them to catch markdowns.",
    updated: "2026-09-30",
    retailerName: "Costco",
    dealsPath: "/deals/costco",
    shopPath: "/shop/costco",
    featurePath: "/features/price-alerts",
    intro:
      "Costco doesn't advertise most of its markdowns, but members have long noticed that the cents on a price sign follow a pattern. None of this is official Costco policy, but the codes below are widely reported and can help you spot a clearance price at a glance.",
    sections: [
      {
        heading: "Commonly reported Costco price endings",
        bullets: [
          ".99 — regular price.",
          ".97 — a markdown, often an item being cleared.",
          ".00 or .88 — a manager's markdown at that warehouse, often for returns, floor models or leftover stock.",
          ".49, .79 or .89 — commonly a manufacturer's discount or special buy.",
          "Asterisk (*) in the corner of the sign — the item isn't being reordered, so when it's gone, it's gone.",
        ],
      },
      {
        heading: "How to use the codes",
        bullets: [
          "Treat .97 plus an asterisk as a strong clearance signal — it's often the lowest price the item will reach.",
          "Manager markdowns (.00 / .88) are warehouse-specific and quantities are usually small.",
          "Costco adjusts prices after purchase within its price-adjustment window in many cases; check the current policy at your warehouse.",
        ],
      },
      {
        heading: "How DealHawk helps",
        paragraphs: [
          "DealHawk tracks Costco price drops with a price history for each item, so you can see how low something has gone before and get alerted when it drops again.",
        ],
      },
    ],
    faq: [
      {
        q: "What does a price ending in .97 mean at Costco?",
        a: "Members commonly report that .97 marks a markdown, often on an item being cleared. Costco doesn't officially confirm it.",
      },
      {
        q: "What does the asterisk on a Costco price sign mean?",
        a: "It's widely reported to mean the item won't be reordered, so once current stock sells out it won't come back.",
      },
      {
        q: "What does a price ending in .00 or .88 mean at Costco?",
        a: "These are commonly reported as manager markdowns specific to that warehouse, such as returns, floor models or leftover stock.",
      },
    ],
  },
  // Answer-style pages for the questions people put to search engines and AI
  // assistants ("best app for…", "how do I find…"). They name the other options
  // honestly and disclose that DealHawk wrote them; competitor details stay to
  // what's on the competitor's own site, without prices or coverage claims that go stale.
  {
    slug: "best-home-depot-clearance-apps",
    title: "Best Apps for Home Depot Clearance & Penny Items",
    h1: "The best apps for finding Home Depot clearance and penny items",
    description:
      "The tools shoppers use to find Home Depot clearance, .03 markdowns and penny items: the Home Depot app, community penny lists, and store-wide clearance trackers like DealHawk.",
    updated: "2026-10-30",
    retailerName: "Home Depot",
    dealsPath: "/deals/home-depot",
    shopPath: "/shop/home_depot",
    featurePath: "/features/in-store-deals",
    intro:
      "Short answer: use the Home Depot app to check a single item's price and stock, community penny lists for leads, and a clearance tracker such as DealHawk to see what's actually marked down at the stores near you without knowing the SKU first. Most serious clearance shoppers use all three. (DealHawk wrote this guide, so weigh our section accordingly.)",
    sections: [
      {
        heading: "Why Home Depot clearance is hard to find",
        paragraphs: [
          "Home Depot clearance is set store by store. The same item can be full price at one store and marked down to a .03 price a few miles away, and those in-store markdowns usually don't show on homedepot.com. Penny items never show at all: they're flagged for removal, so the website treats them as unavailable.",
          "That means the right tool depends on whether you already know the item you want, or want to know what's on clearance at your store.",
        ],
      },
      {
        heading: "1. The Home Depot app (free): best for checking one item",
        bullets: [
          "Scan a barcode or search a SKU to see the price and on-hand count at the store you've selected.",
          "Good for confirming a lead before you drive, or checking an item on the shelf.",
          "Limit: it answers \"what does this item cost here?\", not \"what's on clearance here?\". There's no clearance-only view across a store, and penny items typically show as unavailable rather than $0.01.",
        ],
      },
      {
        heading: "2. Community penny lists and groups (free): best for leads",
        bullets: [
          "Facebook groups, Reddit and penny-list sites share SKUs other shoppers have found at a penny or a .03 price.",
          "Good for learning which kinds of items are going through clearance right now.",
          "Limit: lists are national and penny items differ by store, so a lot of what's listed won't be at yours. Lists also lag: by the time an item is posted, it's often been pulled.",
        ],
      },
      {
        heading: "3. DealHawk: best for seeing a whole store's clearance",
        bullets: [
          "Tracks Home Depot clearance at well over a thousand stores and shows, for the stores you pick, each marked-down item with its in-store price, how many are on hand and where it is in the store.",
          "Flags items that look like they've hit the end of their markdown cycle (clearance items still showing stock but no longer sellable online), which is where penny items come from.",
          "Price history and alerts, so you can see how low an item has gone and get notified when it drops at your store.",
          "Free to browse online deals, the guides and tools such as the Home Depot price decoder and penny checker. Exact local prices and stock are part of the paid plans, available on web, iPhone and Android.",
        ],
      },
      {
        heading: "Which should you use?",
        bullets: [
          "Hunting one specific item: the Home Depot app.",
          "Learning what's going to a penny this week: community lists, then check them against your own stores.",
          "Deciding which of your nearby stores is worth the drive today: a store-wide clearance tracker such as DealHawk.",
        ],
      },
    ],
    faq: [
      {
        q: "What is the best app for Home Depot clearance?",
        a: "It depends on what you need. The Home Depot app is best for checking one item's local price and stock. Community penny lists are good for leads. A clearance tracker like DealHawk shows every marked-down item at the stores near you with price, stock and location, without needing the SKU first.",
      },
      {
        q: "Is there an app that shows Home Depot penny items?",
        a: "Home Depot's own app doesn't, because penny items are flagged for removal and usually show as unavailable. DealHawk flags likely penny items per store from its clearance crawl, and its free Home Depot penny checker tells you whether a SKU is showing penny signals.",
      },
      {
        q: "Can I see Home Depot clearance online?",
        a: "Only partly. homedepot.com shows some online clearance, but in-store markdowns are set by each store and usually don't appear on the website. You need a store-level tool to see them.",
      },
    ],
  },
  {
    slug: "best-penny-shopping-apps",
    title: "Best Apps for Penny Shopping (Dollar General & Home Depot)",
    h1: "The best apps and tools for penny shopping",
    description:
      "How penny shoppers find $0.01 items at Dollar General and Home Depot: retailer apps, community penny lists, and tools that confirm penny prices store by store.",
    updated: "2026-10-30",
    retailerName: "Penny shopping",
    featurePath: "/features/in-store-deals",
    intro:
      "Penny items are products a retailer has flagged for removal, so they ring up at $0.01 but are never advertised. The two chains penny shoppers focus on are Dollar General and Home Depot. To find them you need leads (community penny lists), a way to check your own store (the retailer's app), and ideally a tool that confirms penny prices across many stores (such as DealHawk, which wrote this guide).",
    sections: [
      {
        heading: "Why penny items need special tools",
        paragraphs: [
          "Because a penny price is an internal \"pull this item\" flag, there's no penny sign on the shelf, nothing in the weekly ad and no penny section on the retailer's website. Penny items also differ by store: an item can be a penny at one store and still on the shelf at full clearance price at another.",
        ],
      },
      {
        heading: "Community penny lists (free)",
        bullets: [
          "Facebook groups, Reddit communities and penny-list websites post items shoppers report ringing up at a penny, often refreshed weekly for Dollar General.",
          "Strength: lots of eyes and quick word of mouth.",
          "Weakness: many entries are unconfirmed or copied from other lists, and a national list doesn't tell you what's at your store.",
        ],
      },
      {
        heading: "Retailer apps (free)",
        bullets: [
          "The Dollar General app's price scanner and the Home Depot app's in-store mode let you check items at your store.",
          "Strength: it's the retailer's own data for your store.",
          "Weakness: you have to know what to scan, and penny items often show as unavailable or with no price instead of $0.01.",
        ],
      },
      {
        heading: "DealHawk",
        bullets: [
          "The free Dollar General penny list shows items that rang up at $0.01 in Dollar General's own store price checks during DealHawk's daily crawl of hundreds of stores, not items copied from rumor lists.",
          "The free Home Depot penny checker tells you whether a SKU is showing penny signals in DealHawk's crawl of well over a thousand Home Depot stores.",
          "Paid plans show clearance at your own stores with price, stock and location, flag likely penny items, and include a members-only Discord.",
        ],
      },
      {
        heading: "A simple penny routine",
        bullets: [
          "Check a confirmed list (DealHawk's DG penny list or a trusted group) early in the week; Tuesday is the most-cited day for new Dollar General penny items.",
          "Narrow it to items that are actually showing at your stores.",
          "Go, look in clearance aisles, end caps and sections being reset, and scan before you get in line.",
          "If the cashier or manager says no, accept it. Penny items aren't officially for sale at either chain.",
        ],
      },
    ],
    faq: [
      {
        q: "What is the best app for penny shopping?",
        a: "There isn't one app that does everything. Use the retailer's own app to check your store, community lists for leads, and a tool like DealHawk to confirm which items are actually ringing up at $0.01 and at which stores.",
      },
      {
        q: "Is there a free Dollar General penny list?",
        a: "Yes. Community groups share weekly lists, and DealHawk publishes a free Dollar General penny list of items confirmed at $0.01 in Dollar General's own store price checks, updated daily.",
      },
      {
        q: "Is penny shopping allowed?",
        a: "Penny items are meant to be pulled, so Dollar General and Home Depot can both decline to sell them. Many stores do ring them up, but it's at the store's discretion. Be polite and don't ask staff for penny lists.",
      },
    ],
  },
  {
    slug: "find-clearance-near-me",
    title: "How to Find Clearance Items at Stores Near You",
    h1: "How to find clearance items at stores near you",
    description:
      "Why store clearance rarely shows online, how to find local markdowns at Walmart, Home Depot, Target, Lowe's and Dollar General, and the tools that show them store by store.",
    updated: "2026-10-30",
    retailerName: "In-store clearance",
    featurePath: "/features/in-store-deals",
    intro:
      "Most in-store clearance never appears on a retailer's website, because each store marks down its own leftover stock on its own schedule. To find it you either walk the store, check items one at a time in the retailer's app, or use a tracker that reads store inventory and shows what's marked down at the stores near you.",
    sections: [
      {
        heading: "Why clearance is local",
        paragraphs: [
          "Retailers set one online price, but in-store clearance is decided store by store based on what's left after a season, a reset or a discontinued product. The same item can be full price at one Walmart and 75% off at another across town. Those local markdowns are why \"hidden clearance\" exists: the price is real at the register but invisible online.",
        ],
      },
      {
        heading: "Where to look in the store",
        bullets: [
          "Clearance end caps and the back of each department, where markdowns get consolidated.",
          "Seasonal aisles right after a holiday, and sections being reset to a new layout.",
          "Items in old packaging next to new packaging.",
          "Price tags with telltale endings. Shopper communities commonly report patterns such as .03 at Home Depot or .97 at Costco; see the store guides for each chain.",
        ],
      },
      {
        heading: "Ways to find clearance without walking every aisle",
        bullets: [
          "Retailer apps: scan or search an item to see its price and stock at your store. Good for one item at a time.",
          "Inventory-check websites: look up a specific SKU across nearby stores. Useful when you already know the item.",
          "Deal communities: Discord servers, Facebook groups and Reddit post finds, but they're national and lag behind.",
          "Store-wide clearance trackers such as DealHawk: pick your stores and see every marked-down item with its price, how many are left and where it is in the store.",
        ],
      },
      {
        heading: "Stores where this matters most",
        bullets: [
          "Walmart: hidden clearance often rings up below the shelf tag.",
          "Home Depot and Lowe's: deep seasonal and tool clearance, plus Home Depot penny items.",
          "Target: clearance stickers drop in steps (commonly reported as 30%, 50%, 70%, then 90%).",
          "Dollar General: weekly penny items and stacks with digital coupons.",
          "Costco, Best Buy, Tractor Supply, Northern Tool and Sam's Club: local markdowns, open-box and floor models.",
        ],
      },
      {
        heading: "How DealHawk helps",
        paragraphs: [
          "DealHawk checks store inventory directly at Walmart, Home Depot, Lowe's, Target, Costco, Best Buy, Dollar General, Tractor Supply, Northern Tool and Sam's Club, and shows the clearance at the stores you choose with exact in-store price, stock and aisle. Online deals, the guides and the free tools are open to everyone; local prices are part of the paid plans. DealHawk is not affiliated with these retailers, and prices change constantly, so always confirm in store.",
        ],
      },
    ],
    faq: [
      {
        q: "How do I find clearance items near me?",
        a: "Check the clearance end caps and seasonal aisles in person, use the retailer's app to check specific items at your store, or use a store-level tracker such as DealHawk that lists every marked-down item at the stores you pick with price, stock and location.",
      },
      {
        q: "Why isn't in-store clearance on the retailer's website?",
        a: "Each store marks down its own leftover stock on its own schedule, so the website usually shows one national price. Local markdowns only show at the register or in store-level inventory data.",
      },
      {
        q: "Is there an app that shows clearance at all stores near me?",
        a: "Retailer apps show one store and one item at a time. DealHawk shows clearance across your chosen stores at ten US chains, including Walmart, Home Depot, Target, Lowe's and Dollar General, on web, iPhone and Android.",
      },
    ],
  },
  {
    slug: "dealhawk-vs-brickseek",
    title: "DealHawk vs BrickSeek: Which Clearance Finder Fits You?",
    h1: "DealHawk vs BrickSeek",
    description:
      "How DealHawk and BrickSeek differ: item lookup versus store-wide clearance, which stores and features each focuses on, and which one fits how you shop.",
    updated: "2026-10-30",
    retailerName: "Comparisons",
    featurePath: "/features/in-store-deals",
    intro:
      "BrickSeek and DealHawk both help you find deals at physical stores, but they start from opposite ends. BrickSeek is best known for its inventory checker: you enter an item and a ZIP code and see stock and prices at nearby stores. DealHawk starts from your stores: it shows everything on clearance at the stores you pick, so you don't need to know the item first. DealHawk wrote this comparison; check BrickSeek's own site for its current features, coverage and pricing.",
    sections: [
      {
        heading: "The core difference",
        bullets: [
          "Item-first (BrickSeek's inventory checker): best when you already know the SKU or product and want to know where it's in stock or cheapest.",
          "Store-first (DealHawk): best when you want to know what's marked down at your Walmart, Home Depot or Dollar General today, including items you'd never have thought to look up.",
        ],
      },
      {
        heading: "What DealHawk focuses on",
        bullets: [
          "In-store clearance at Walmart, Home Depot, Lowe's, Target, Costco, Best Buy, Dollar General, Tractor Supply, Northern Tool and Sam's Club, with exact in-store price, stock count and aisle for each item at your stores.",
          "Penny items: a free Dollar General penny list confirmed by the stores' own price checks, a Home Depot penny checker, and per-store flags for likely penny items.",
          "Price history and price alerts per item and store.",
          "Native iPhone and Android apps plus web, and a members-only Discord on paid plans.",
          "Free to browse online deals, guides and tools; local store data is part of plans starting at $34.99/month (half off the first month).",
        ],
      },
      {
        heading: "When BrickSeek may suit you better",
        bullets: [
          "You mostly track down specific items by SKU rather than browsing a store's clearance.",
          "You want an item lookup at a retailer DealHawk doesn't cover in store.",
          "You're mainly after online deals and want a free deals feed.",
        ],
      },
      {
        heading: "When DealHawk may suit you better",
        bullets: [
          "You shop a few local stores and want to see all of their clearance at once.",
          "You hunt penny items at Dollar General or Home Depot.",
          "You want stock counts and aisle locations before you drive, plus alerts when an item drops at your store.",
        ],
      },
    ],
    faq: [
      {
        q: "Is DealHawk like BrickSeek?",
        a: "Both help you find in-store deals, but BrickSeek is best known for looking up a specific item's stock and price at nearby stores, while DealHawk shows all the clearance at the stores you choose, with price, stock and aisle, without needing the item first.",
      },
      {
        q: "What is a good BrickSeek alternative for in-store clearance?",
        a: "DealHawk is an alternative for store-wide clearance at Walmart, Home Depot, Lowe's, Target, Costco, Best Buy, Dollar General, Tractor Supply, Northern Tool and Sam's Club, including penny items at Dollar General and Home Depot.",
      },
      {
        q: "Can I use both?",
        a: "Yes. Many shoppers use a store-wide tracker to spot what's on clearance locally and an item lookup to check one specific product across more stores.",
      },
    ],
  },
];

export function getGuideBySlug(slug: string): Guide | undefined {
  return GUIDES.find((g) => g.slug === slug);
}

export function guideJsonLd(guide: Guide, path: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.h1,
    description: guide.description,
    dateModified: guide.updated,
    author: { "@type": "Organization", name: "DealHawk", url: "https://dealhawkpro.io" },
    publisher: {
      "@type": "Organization",
      name: "DealHawk",
      url: "https://dealhawkpro.io",
      logo: { "@type": "ImageObject", url: "https://dealhawkpro.io/logo-240.png" },
    },
    mainEntityOfPage: `https://dealhawkpro.io${path}`,
  };
}
